import type { KursDto } from "@shared/kosztyTypy.js";

/**
 * Koszty w interfejsie: etykiety i format po polsku, bez DOM i bez Reacta.
 *
 * Liczby: spacja jako separator tysiecy (zwykla, nie twarda: zakaz NBP w zrodlach), przecinek dziesietny. Kwoty
 * ponizej 0,10 pokazujemy z czterema miejscami (0,0123), bo koszt jednej oceny to czesto ulamek centa i "0,01"
 * ukrywaloby roznice miedzy modelami. Zaokraglamy WYLACZNIE przy wyswietlaniu: sumy liczy serwer z niezaokraglonych.
 */

const ETYKIETY_ETAPOW: Readonly<Record<string, string>> = {
  transkrypcja: "Transkrypcja",
  brzmienie: "Ocena brzmienia",
  wymiary: "Wymiary głosu (SER)",
  soczewka: "Ocena wg metodyki",
  podzial: "Podział tekstu",
  "nazwy-mowcow": "Nazwy mówców",
  trening: "Trening",
  diagnostyka: "Diagnostyka",
  porownanie: "Porównanie dostawców",
};

const ETYKIETY_DOSTAWCOW: Readonly<Record<string, string>> = {
  openai: "OpenAI",
  gemini: "Google Gemini",
  elevenlabs: "ElevenLabs",
  qwen: "Qwen",
  ser: "SER (własny hosting)",
};

export function etykietaEtapu(etap: string): string {
  return ETYKIETY_ETAPOW[etap] ?? etap;
}

export function etykietaDostawcy(dostawca: string): string {
  return ETYKIETY_DOSTAWCOW[dostawca] ?? dostawca;
}

function grupujTysiace(cyfry: string): string {
  return cyfry.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** 1234567 -> "1 234 567"; z miejscami: 1234.5 -> "1 234,50". */
export function formatujLiczbe(n: number, miejsca = 0): string {
  const [calkowita, ulamek] = Math.abs(n).toFixed(miejsca).split(".");
  return `${n < 0 ? "-" : ""}${grupujTysiace(calkowita)}${ulamek ? `,${ulamek}` : ""}`;
}

/** Cyfry kwoty: od 0,10 dwa miejsca, ponizej cztery. Wywolujacy obsluguje zero i wartosci ponizej 0,0001. */
function cyfryKwoty(wartosc: number): string {
  return formatujLiczbe(wartosc, wartosc >= 0.1 ? 2 : 4);
}

export function formatujUsd(usd: number): string {
  if (usd === 0) return "$0,00";
  if (usd < 0.0001) return "<$0,0001";
  return `$${cyfryKwoty(usd)}`;
}

/** Zlote z dolarow po kursie; kurs podaje serwer razem z data (KursDto). */
export function formatujPln(usd: number, kurs: number): string {
  const pln = usd * kurs;
  if (pln === 0) return "0,00 PLN";
  if (pln < 0.0001) return "<0,0001 PLN";
  return `${cyfryKwoty(pln)} PLN`;
}

/** "1 234 / 56": tokeny wejscia i wyjscia; brak po jednej stronie to kreska, nie zero. */
export function formatujTokeny(wejscie: number | null, wyjscie: number | null): string {
  if (wejscie === null && wyjscie === null) return "-";
  const czesc = (n: number | null): string => (n === null ? "-" : formatujLiczbe(n));
  return `${czesc(wejscie)} / ${czesc(wyjscie)}`;
}

export interface KosztDoPokazania {
  /** Glowna wartosc w USD albo null, gdy nie ma czego pokazac (wtedy kreska z powodem). */
  usd: string | null;
  /** Druga linia w zlotych albo null (brak kursu albo brak kwoty). */
  pln: string | null;
  /** Prawda, gdy czesci kosztu nie znamy: kwota ma prefiks "od" i plakietke. */
  niepelny: boolean;
  /** Powod do dymka: czemu nie ma kwoty albo czemu jest niepelna. */
  powod: string | null;
}

/**
 * Jak pokazac koszt: kompletny to zwykla kwota, niepelny to "od <kwota>" (dolne ograniczenie) z powodem, a brak kwoty
 * to kreska z powodem. Zero nigdy nie zastepuje "nie wiemy".
 */
export function kosztDoPokazania(
  usd: number | null,
  kompletny: boolean,
  powody: readonly string[],
  kurs: KursDto | null,
): KosztDoPokazania {
  if (usd === null) {
    return {
      usd: null,
      pln: null,
      niepelny: false,
      powod: powody.length > 0 ? powody.join("; ") : "brak danych do policzenia kosztu",
    };
  }
  const prefiks = kompletny ? "" : "od ";
  return {
    usd: `${prefiks}${formatujUsd(usd)}`,
    pln: kurs ? `${prefiks}${formatujPln(usd, kurs.kurs)}` : null,
    niepelny: !kompletny,
    powod: kompletny ? null : powody.length > 0 ? powody.join("; ") : "część wywołań nie ma policzonego kosztu",
  };
}

/** "Kurs 3,8881 PLN/USD z dnia 2026-10-02 (NBP, tabela A, kurs średni USD)". */
export function opisKursu(kurs: KursDto): string {
  return `Kurs ${formatujLiczbe(kurs.kurs, 4)} PLN/USD z dnia ${kurs.data} (${kurs.zrodlo}).`;
}
